import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import { forecastPolicyService } from "../../utils/services/forecastPolicyService";

const font = { fontFamily: "poppins" };

const COUNTRIES = [
  ["US", "United States"],
  ["CA", "Canada"],
  ["GB", "United Kingdom"],
  ["IN", "India"],
  ["AU", "Australia"],
  ["DE", "Germany"],
  ["FR", "France"],
  ["MX", "Mexico"],
  ["AE", "United Arab Emirates"],
  ["SG", "Singapore"],
];

const INTERVALS = {
  80: {
    title: "80% range",
    text: "About 8 in 10 periods land inside the range. A tighter band for day-to-day planning.",
  },
  90: {
    title: "90% range",
    text: "About 9 in 10 periods land inside. A good middle ground when stock-outs are costly.",
  },
  95: {
    title: "95% range",
    text: "Almost every period lands inside. The widest band, for high-value or long lead-time items.",
  },
};

const DISCOUNT_FILL = {
  zero: {
    title: "Assume no discount",
    text: "Plan future months at full price unless you enter a discount when you run the forecast.",
  },
  last: {
    title: "Keep the latest discount",
    text: "Assume the discount you run today carries on. Use this if you discount all the time.",
  },
  drop: {
    title: "Don't use discounts",
    text: "Forecast from sales alone, ignoring past and future discounts.",
  },
};

const METRICS = {
  wape: {
    title: "Error per unit sold (WAPE)",
    text: "Total units the forecast missed, divided by total units sold. Recommended: high sellers weigh more and months with no sales don't break it.",
  },
  mase: {
    title: "Error compared with a simple guess (MASE)",
    text: "Below 1 means the model beats repeating last season. Good when comparing items of very different sizes.",
  },
  smape: {
    title: "Symmetric % error (sMAPE)",
    text: "Average % error that treats over- and under-forecasting the same.",
  },
  mape: {
    title: "Average % error (MAPE)",
    text: "The classic % error. Skips months with no sales, so it can flatter slow sellers.",
  },
  wape_total: {
    title: "Error on the total for the period",
    text: "How far the forecast total for the whole horizon is from what sold. Recommended for slow sellers, where the total matters more than each month.",
  },
};

// ---- How the forecast is chosen, as plain questions ------------------------
// Each answer maps to technical settings on the server (forecast-policy.js
// BUSINESS); the raw settings are only shown to admins, under "Technical
// details".
const ACCURACY_FOCUS = {
  each_period: {
    title: "Each month as close as possible",
    text: "Picks the method that gets each month's (or week's) sales closest. Best for day-to-day planning.",
  },
  order_totals: {
    title: "The total for the period right",
    text: "Picks the method that gets the total over the coming months closest. Best when you order in large batches.",
  },
  fair_across_sizes: {
    title: "Fair to small and big sellers",
    text: "Judges every product on its own scale, so slow sellers count as much as best sellers.",
  },
};

const CAUTION = {
  best: {
    title: "Use the most accurate method",
    text: "Whichever method predicted your past sales best is used, even if it only just beat last year's pattern.",
  },
  clear: {
    title: "Only when clearly better",
    text: "A method is used only if it is at least 5% more accurate than repeating last year. Otherwise last year's pattern is used.",
  },
  cautious: {
    title: "Only when much better",
    text: "Last year's pattern is used unless a method is at least 15% more accurate. The steadiest choice.",
  },
};

const THOROUGHNESS = {
  quick: {
    title: "Quick",
    text: "Tests each method twice. Faster, and works with a shorter sales history.",
  },
  standard: {
    title: "Standard",
    text: "Tests each method three times. Suits most products.",
  },
  thorough: {
    title: "Thorough",
    text: "Tests each method five times. A more reliable choice, but needs a longer sales history and takes longer.",
  },
};

const METHOD_STYLE = {
  simple: {
    title: "Simple and steady",
    text: "Only a few straightforward methods. Forecasts change less from run to run and are easy to explain.",
  },
  recommended: {
    title: "Recommended mix",
    text: "A balanced set of proven methods for each kind of demand.",
  },
  advanced: {
    title: "Widest choice",
    text: "Every method we have, including ones that follow complex trends. Can find a better forecast, but runs slower.",
  },
};

// Business choice → technical settings it controls (mirrors the server's LINKED).
const LINKED = {
  accuracy_focus: ["selection_metric", "intermittent_metric"],
  caution: ["min_improvement_over_benchmark"],
  thoroughness: ["backtest_windows"],
  method_style: ["segments"],
};
const TECHNICAL = Object.values(LINKED).flat();

const Question = ({ title, text, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Box>
      <Typography sx={{ ...font, fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>{title}</Typography>
      {text && (
        <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)", mt: 0.25 }}>{text}</Typography>
      )}
    </Box>
    {children}
  </Box>
);

const Section = ({ title, subtitle, locked, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ ...font, fontWeight: 600, fontSize: 15, color: "var(--text-primary)" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)", mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {locked && (
        <Chip
          size="small"
          icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
          label="Managed by your DropSkip team"
          sx={{ ...font, fontSize: 11 }}
        />
      )}
    </Box>
    {children}
  </Box>
);

// A row of selectable cards: title + one line of explanation each.
const ChoiceCards = ({ value, options, onChange, disabled, defaultValue }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1 }}>
    {Object.entries(options).map(([key, o]) => {
      const selected = String(value) === String(key);
      return (
        <Box
          key={key}
          role="radio"
          aria-checked={selected}
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && onChange(key)}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onChange(key);
            }
          }}
          sx={{
            p: 1.5,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: selected ? "primary.main" : "var(--border-color)",
            backgroundColor: selected ? "var(--hover-bg)" : "transparent",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled && !selected ? 0.6 : 1,
            outline: "none",
            "&:focus-visible": { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ ...font, fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>
              {o.title}
            </Typography>
            {String(defaultValue) === String(key) && (
              <Chip size="small" label="Recommended" sx={{ ...font, height: 18, fontSize: 10 }} />
            )}
          </Box>
          <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)", mt: 0.5 }}>{o.text}</Typography>
        </Box>
      );
    })}
  </Box>
);

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Forecast settings for one client. Used from the admin client list and from
 * the client's own account settings. Everything is asked as plain business
 * questions anyone can answer; admins also get the raw technical settings
 * those answers map to, collapsed under "Technical details".
 */
const ForecastSettingsDialog = ({ open, onClose, clientId, clientName }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(null); // server response
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  // Technical settings an admin changed in this session (sent as overrides),
  // and whether to drop every existing override.
  const [touched, setTouched] = useState(() => new Set());
  const [clearOverrides, setClearOverrides] = useState(false);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    try {
      const res = await forecastPolicyService.get(clientId);
      setView(res);
      setTouched(new Set());
      setClearOverrides(false);
      setDraft({
        ...res.settings,
        events: (res.settings.events || []).map((e) => ({
          name: e.name,
          start: toDateInput(e.start),
          end: toDateInput(e.end),
        })),
      });
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't load the forecast settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const canEdit = (field) => !!view?.editable?.includes(field);
  const isAdmin = canEdit("segments");
  const defaults = view?.defaults || {};
  const options = view?.options || {};

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  // A business choice also previews the technical values it leads to (the
  // server clears matching overrides when it saves the choice).
  const setChoice = (field, value) => {
    const mapped = options.business_map?.[field]?.[value] || {};
    setDraft((d) => {
      const next = { ...d, [field]: value };
      for (const tech of LINKED[field] || []) {
        if (tech === "segments" && mapped.segments) next.segments = mapped.segments;
        else if (tech in mapped) next[tech] = mapped[tech];
      }
      return next;
    });
    setTouched((t) => {
      const next = new Set(t);
      (LINKED[field] || []).forEach((tech) => next.delete(tech));
      return next;
    });
  };
  // An admin editing a technical setting directly → saved as an override.
  const setTechnical = (field, value) => {
    set(field, value);
    setTouched((t) => new Set(t).add(field));
  };
  const overridden = (field) =>
    touched.has(field) || (!clearOverrides && (view?.overrides || []).includes(field));

  // Only send fields that differ from what was loaded. Technical settings
  // are only sent when an admin changed them directly (or cleared them) —
  // never just because a business choice moved them.
  const changes = useMemo(() => {
    if (!view || !draft) return {};
    const original = {
      ...view.settings,
      events: (view.settings.events || []).map((e) => ({
        name: e.name,
        start: toDateInput(e.start),
        end: toDateInput(e.end),
      })),
    };
    const out = {};
    for (const field of view.editable || []) {
      if (TECHNICAL.includes(field)) continue;
      if (!sameValue(draft[field], original[field])) out[field] = draft[field];
    }
    if (clearOverrides) {
      (view.overrides || []).forEach((field) => {
        out[field] = null;
      });
    }
    touched.forEach((field) => {
      out[field] = draft[field];
    });
    return out;
  }, [view, draft, touched, clearOverrides]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await forecastPolicyService.save(clientId, changes);
      toast.success("Forecast settings saved. They apply to the next forecast you run.");
      setView(res);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't save the forecast settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await forecastPolicyService.reset(clientId);
      toast.success("Forecast settings reset to the recommended defaults.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't reset the forecast settings.");
    } finally {
      setSaving(false);
    }
  };


  const customisedCount = view?.customised?.length || 0;
  const changedCount = Object.keys(changes).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ ...font, fontWeight: 700, fontSize: 18, color: "var(--text-primary)" }}>
          Forecast settings{clientName ? ` · ${clientName}` : ""}
        </Typography>
        <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)", mt: 0.5 }}>
          These settings shape every demand forecast for this account. Anything you leave as is uses our
          recommended default.
        </Typography>
        {view && (
          <Chip
            size="small"
            sx={{ ...font, mt: 1, fontSize: 11 }}
            color={customisedCount ? "primary" : "default"}
            variant={customisedCount ? "filled" : "outlined"}
            label={customisedCount ? `${customisedCount} setting(s) customised` : "Using recommended defaults"}
          />
        )}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 3, backgroundColor: "var(--bg-paper)" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && !loading && <Alert severity="error">{error}</Alert>}

        {draft && !loading && (
          <>
            {/* -------------------------------------------------- calendar */}
            <Section
              title="Holidays and sales events"
              subtitle="Demand jumps around holidays and promotions. Telling the forecast when they happen lets it learn how much they lift sales, and plan for the next one."
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!!draft.calendar_enabled}
                    onChange={(e) => set("calendar_enabled", e.target.checked)}
                    disabled={!canEdit("calendar_enabled")}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ ...font, fontSize: 13, color: "var(--text-primary)" }}>
                      Account for public holidays and shopping peaks
                    </Typography>
                    <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)" }}>
                      Includes Black Friday to Cyber Monday, Prime Day, Valentine's Day, Mother's and Father's Day and
                      the 15–24 December gifting rush.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", ml: 0, "& .MuiSwitch-root": { mt: -0.5 } }}
              />

              <TextField
                id="forecast-calendar-country"
                select
                size="small"
                label="Holiday calendar"
                value={draft.calendar_country}
                onChange={(e) => set("calendar_country", e.target.value)}
                disabled={!canEdit("calendar_country") || !draft.calendar_enabled}
                helperText="Whose public holidays apply. Pick the country where most of your customers are."
                sx={{ maxWidth: 360 }}
              >
                {(COUNTRIES.some(([c]) => c === draft.calendar_country)
                  ? COUNTRIES
                  : [[draft.calendar_country, draft.calendar_country], ...COUNTRIES]
                ).map(([code, name]) => (
                  <MenuItem key={code} value={code}>
                    {name} ({code})
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <EventOutlinedIcon sx={{ fontSize: 18, color: "var(--text-secondary)" }} />
                  <Typography sx={{ ...font, fontWeight: 600, fontSize: 13, color: "var(--text-primary)", flex: 1 }}>
                    Your promotions and events
                  </Typography>
                </Box>
                <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)", mb: 1 }}>
                  Taken from the promo days on the Client Config page (Lost Sales events). Add or change them there;
                  active ones are used by the next forecast.
                </Typography>

                {(view.promo_events || []).length === 0 ? (
                  <Box
                    sx={{
                      border: "1px dashed var(--border-color)",
                      borderRadius: "8px",
                      p: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)" }}>
                      No active promo days on the Client Config page.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    component="ul"
                    data-testid="forecast-promo-events"
                    sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {view.promo_events.map((ev, i) => (
                      <Box
                        component="li"
                        key={`${ev.name}-${ev.start}-${i}`}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          px: 1.5,
                          py: 0.75,
                          border: "1px solid var(--border-color)",
                          borderRadius: "6px",
                        }}
                      >
                        <Typography sx={{ ...font, fontSize: 13, color: "var(--text-primary)" }}>{ev.name}</Typography>
                        <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                          {ev.end && ev.end !== ev.start ? `${ev.start} – ${ev.end}` : ev.start}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Section>

            <Divider />

            {/* -------------------------------------------------- range */}
            <Section
              title="Forecast range"
              subtitle="Every forecast comes with a low and a high estimate, built from how wrong past forecasts were. Choose how wide that range should be."
            >
              <ChoiceCards
                value={draft.interval_level}
                options={INTERVALS}
                defaultValue={defaults.interval_level}
                onChange={(v) => set("interval_level", Number(v))}
                disabled={!canEdit("interval_level")}
              />
            </Section>

            <Divider />

            {/* -------------------------------------------------- discounts */}
            <Section
              title="Future discounts"
              subtitle="Past discounts help explain past sales. For the months ahead, what should the forecast assume when you haven't entered a discount?"
            >
              <ChoiceCards
                value={draft.discount_future_fill}
                options={DISCOUNT_FILL}
                defaultValue={defaults.discount_future_fill}
                onChange={(v) => set("discount_future_fill", v)}
                disabled={!canEdit("discount_future_fill")}
              />
            </Section>

            <Divider />

            {/* -------------------------------------------------- how the forecast is chosen */}
            <Section
              title="How we choose the forecast"
              subtitle="For every product we try several forecasting methods on your own past sales: we hide the most recent months, let each method predict them, and keep the one that came closest. These choices decide how that test is run."
            >
              <Question title="What matters most when we compare forecasts?">
                <ChoiceCards
                  value={draft.accuracy_focus}
                  options={ACCURACY_FOCUS}
                  defaultValue={defaults.accuracy_focus}
                  onChange={(v) => setChoice("accuracy_focus", v)}
                  disabled={!canEdit("accuracy_focus")}
                />
              </Question>

              <Question
                title={'When should a forecasting method replace "same as last year"?'}
                text="Repeating last year's sales is the simple guess every method has to beat."
              >
                <ChoiceCards
                  value={draft.caution}
                  options={CAUTION}
                  defaultValue={defaults.caution}
                  onChange={(v) => setChoice("caution", v)}
                  disabled={!canEdit("caution")}
                />
              </Question>

              <Question title="How thoroughly should each method be tested?">
                <ChoiceCards
                  value={draft.thoroughness}
                  options={THOROUGHNESS}
                  defaultValue={defaults.thoroughness}
                  onChange={(v) => setChoice("thoroughness", v)}
                  disabled={!canEdit("thoroughness")}
                />
              </Question>

              <Question title="Which forecasting methods may we try?">
                <ChoiceCards
                  value={draft.method_style}
                  options={METHOD_STYLE}
                  defaultValue={defaults.method_style}
                  onChange={(v) => setChoice("method_style", v)}
                  disabled={!canEdit("method_style")}
                />
              </Question>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!draft.regressors_enabled}
                    onChange={(e) => set("regressors_enabled", e.target.checked)}
                    disabled={!canEdit("regressors_enabled")}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ ...font, fontSize: 13, color: "var(--text-primary)" }}>
                      Let promotions, discounts and holidays shape the forecast
                    </Typography>
                    <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)" }}>
                      Methods that take your promotions, discounts and holiday calendar into account are tested
                      too. They are only used when they predict your past sales better.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", ml: 0 }}
              />

              {(view?.overrides || []).length > 0 && !clearOverrides && !isAdmin && (
                <Alert severity="info" sx={{ ...font, fontSize: 12 }}>
                  The DropSkip team has fine-tuned some of these settings for your account. Changing a choice above
                  replaces their adjustment for that choice.
                </Alert>
              )}
            </Section>

            {isAdmin && (
              <Accordion
                disableGutters
                sx={{
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px !important",
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <LockOutlinedIcon sx={{ fontSize: 16, color: "var(--text-secondary)" }} />
                    <Typography sx={{ ...font, fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>
                      Technical details (DropSkip team only)
                    </Typography>
                    {TECHNICAL.some(overridden) ? (
                      <Chip size="small" color="warning" label="Overridden" sx={{ ...font, fontSize: 11 }} />
                    ) : (
                      <Chip size="small" variant="outlined" label="Following the choices above" sx={{ ...font, fontSize: 11 }} />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)" }}>
                    The exact settings the choices above lead to. Changing one here overrides the matching choice
                    for this client until someone changes that choice again.
                  </Typography>
                  {TECHNICAL.some(overridden) && (
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setClearOverrides(true);
                          setTouched(new Set());
                          ["accuracy_focus", "caution", "thoroughness", "method_style"].forEach((c) =>
                            setChoice(c, draft[c]),
                          );
                        }}
                        sx={{ ...font, textTransform: "none" }}
                      >
                        Remove overrides and follow the choices above
                      </Button>
                    </Box>
                  )}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <TextField
                  id="forecast-selection-metric"
                  select
                  size="small"
                  label="Judge accuracy by"
                  value={draft.selection_metric}
                  onChange={(e) => setTechnical("selection_metric", e.target.value)}
                  disabled={!canEdit("selection_metric")}
                  helperText={METRICS[draft.selection_metric]?.text}
                >
                  {(options.selection_metric || []).map((m) => (
                    <MenuItem key={m} value={m}>
                      {METRICS[m]?.title || m}
                      {m === defaults.selection_metric ? " · recommended" : ""}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  id="forecast-intermittent-metric"
                  select
                  size="small"
                  label="For slow sellers, judge accuracy by"
                  value={draft.intermittent_metric}
                  onChange={(e) => setTechnical("intermittent_metric", e.target.value)}
                  disabled={!canEdit("intermittent_metric")}
                  helperText={METRICS[draft.intermittent_metric]?.text}
                >
                  {(options.intermittent_metric || []).map((m) => (
                    <MenuItem key={m} value={m}>
                      {METRICS[m]?.title || m}
                      {m === defaults.intermittent_metric ? " · recommended" : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3, mt: 1 }}>
                <Box>
                  <Typography sx={{ ...font, fontSize: 13, color: "var(--text-primary)" }}>
                    Must beat "same as last year" by {Math.round((draft.min_improvement_over_benchmark || 0) * 100)}%
                  </Typography>
                  <Slider
                    value={Math.round((draft.min_improvement_over_benchmark || 0) * 100)}
                    min={0}
                    max={50}
                    step={5}
                    marks={[{ value: 0, label: "0%" }, { value: 25, label: "25%" }, { value: 50, label: "50%" }]}
                    onChange={(_, v) => setTechnical("min_improvement_over_benchmark", v / 100)}
                    disabled={!canEdit("min_improvement_over_benchmark")}
                    aria-label="Required improvement over the simple benchmark"
                  />
                  <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)" }}>
                    If no method beats simply repeating last year's numbers by this much, last year's numbers are used.
                    0% means any improvement counts.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ ...font, fontSize: 13, color: "var(--text-primary)" }}>
                    Test on {draft.backtest_windows} past periods
                  </Typography>
                  <Slider
                    value={draft.backtest_windows}
                    min={options.backtest_windows?.min || 2}
                    max={options.backtest_windows?.max || 6}
                    step={1}
                    marks
                    onChange={(_, v) => setTechnical("backtest_windows", v)}
                    disabled={!canEdit("backtest_windows")}
                    aria-label="Number of test windows"
                  />
                  <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)" }}>
                    More tests give a more reliable choice but need more history and take longer. 3 suits most
                    accounts.
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography sx={{ ...font, fontWeight: 600, fontSize: 13, color: "var(--text-primary)", mb: 0.5 }}>
                  Methods tested for each kind of demand
                </Typography>
                <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)", mb: 1 }}>
                  Each product is sorted into one of these groups from its sales history. Only the methods ticked for
                  its group are tested.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {Object.entries(options.segments || {}).map(([segment, label]) => {
                    const selected = draft.segments?.[segment] || [];
                    const isDefault = sameValue(selected, options.business_map?.method_style?.[draft.method_style]?.segments?.[segment] || defaults.segments?.[segment]);
                    return (
                      <Box key={segment} sx={{ border: "1px solid var(--border-color)", borderRadius: "8px", p: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography sx={{ ...font, fontSize: 13, fontWeight: 600, color: "var(--text-primary)", flex: 1 }}>
                            {label}
                          </Typography>
                          {!isDefault && canEdit("segments") && (
                            <Button
                              size="small"
                              sx={{ ...font, textTransform: "none", fontSize: 12 }}
                              onClick={() => setTechnical("segments", { ...draft.segments, [segment]: options.business_map?.method_style?.[draft.method_style]?.segments?.[segment] || defaults.segments[segment] })}
                            >
                              Follow the choice above
                            </Button>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {(isAdmin ? Object.keys(options.models || {}) : selected).map((model) => {
                            const on = selected.includes(model);
                            return (
                              <Chip
                                key={model}
                                size="small"
                                label={options.models?.[model] || model}
                                color={on ? "primary" : "default"}
                                variant={on ? "filled" : "outlined"}
                                onClick={
                                  canEdit("segments")
                                    ? () => {
                                        const next = on ? selected.filter((m) => m !== model) : [...selected, model];
                                        if (next.length === 0) {
                                          toast.info("Keep at least one method for each group.");
                                          return;
                                        }
                                        setTechnical("segments", { ...draft.segments, [segment]: next });
                                      }
                                    : undefined
                                }
                                sx={{ ...font, fontSize: 11 }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {isAdmin && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: "var(--hover-bg)",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, color: "var(--text-secondary)", mt: 0.25 }} />
                <Typography sx={{ ...font, fontSize: 12, color: "var(--text-secondary)" }}>
                  These settings are sent with every forecast request for this client (country, events and policy).
                  Changes apply to the next forecast run; past forecasts are not changed.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        {isAdmin && customisedCount > 0 && (
          <Tooltip title="Remove every customisation for this client">
            <span>
              <Button color="inherit" onClick={handleReset} disabled={saving} sx={{ ...font, textTransform: "none", mr: "auto" }}>
                Reset to recommended
              </Button>
            </span>
          </Tooltip>
        )}
        <Button onClick={onClose} sx={{ ...font, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !changedCount}
          sx={{ ...font, textTransform: "none" }}
        >
          {saving ? "Saving…" : changedCount ? `Save ${changedCount} change${changedCount === 1 ? "" : "s"}` : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForecastSettingsDialog;
